import { useMemo, useState } from 'react'
import {ChevronDown, ChevronUp, Copy, Download, RotateCcw} from 'lucide-react'
import toast from 'react-hot-toast'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import Slider from '@/components/ui/Slider'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import { formatCurrency } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'
import { exportResultsToPdf } from '@/lib/pdfExport'

interface ScheduleRow {
  month: number
  payment: number
  principal: number
  interest: number
  balance: number
}

function buildSchedule(loanAmount: number, monthlyRate: number, basePayment: number, extraPayment: number) {
  const schedule: ScheduleRow[] = []
  let balance = loanAmount
  let month = 0
  let totalInterest = 0

  while (balance > 0.01 && month < 600) {
    month += 1
    const interest = balance * monthlyRate
    let principal = basePayment - interest + extraPayment
    if (principal > balance) principal = balance
    const payment = principal + interest
    balance = Math.max(0, balance - principal)
    totalInterest += interest
    schedule.push({ month, payment, principal, interest, balance })
  }

  return { schedule, totalInterest }
}

const faqItems = [
  { question: 'How is a monthly loan payment calculated?', answer: 'The standard amortization formula is M = P × [r(1+r)^n] / [(1+r)^n - 1], where P is principal, r is the monthly interest rate, and n is the total number of payments.' },
  { question: 'How does an extra principal payment save money?', answer: 'Extra principal payments reduce the outstanding balance faster, which lowers the interest charged in every subsequent month, shortening the loan term and reducing total interest paid.' },
  { question: 'What is an amortization schedule?', answer: 'An amortization schedule breaks down every payment over the life of the loan into principal and interest portions, showing exactly how the remaining balance declines over time.' },
]

function MintLoan() {
  const [loanAmount, setLoanAmount] = useState(25000)
  const [apr, setApr] = useState(6.5)
  const [termYears, setTermYears] = useState(5)
  const [extraPayment, setExtraPayment] = useState(50)
  const [showSchedule, setShowSchedule] = useState(false)
  const [visibleRows, setVisibleRows] = useState(12)

  const results = useMemo(() => {
    const monthlyRate = apr / 100 / 12
    const totalMonths = termYears * 12
    const basePayment = monthlyRate === 0
      ? loanAmount / totalMonths
      : (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1)

    const base = buildSchedule(loanAmount, monthlyRate, basePayment, 0)
    const withExtra = buildSchedule(loanAmount, monthlyRate, basePayment, extraPayment)

    const totalCostBase = base.schedule.reduce((sum, r) => sum + r.payment, 0)
    const totalCostExtra = withExtra.schedule.reduce((sum, r) => sum + r.payment, 0)
    const monthsSaved = base.schedule.length - withExtra.schedule.length
    const interestSaved = base.totalInterest - withExtra.totalInterest

    return {
      basePayment,
      totalInterestBase: base.totalInterest,
      totalCostBase,
      schedule: withExtra.schedule,
      totalInterestExtra: withExtra.totalInterest,
      totalCostExtra,
      monthsSaved,
      interestSaved,
    }
  }, [loanAmount, apr, termYears, extraPayment])

  const handleReset = () => {
    setLoanAmount(25000)
    setApr(6.5)
    setTermYears(5)
    setExtraPayment(50)
    toast.success('Reset to default values')
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(`Mint Calculator - Loan Summary\nMonthly Payment: ${formatCurrency(results.basePayment)}\nTotal Interest: ${formatCurrency(results.totalInterestExtra)}\nTotal Cost: ${formatCurrency(results.totalCostExtra)}\nInterest Saved: ${formatCurrency(results.interestSaved)}`)
    toast.success('Summary copied to clipboard')
  }

  const handleDownloadPdf = () => {
    exportResultsToPdf({
      title: 'Mint Loan & Amortization Report',
      subtitle: 'Loan payoff summary generated by Mint Calculator',
      fileName: 'mint-calculator-loan-amortization.pdf',
      sections: [
        {
          heading: 'Loan Inputs',
          rows: [
            ['Loan Amount', formatCurrency(loanAmount)],
            ['Annual Interest Rate (APR)', `${apr.toFixed(1)}%`],
            ['Loan Term', `${termYears} years`],
            ['Extra Monthly Principal Payment', formatCurrency(extraPayment)],
          ],
        },
        {
          heading: 'Payoff Summary',
          rows: [
            ['Monthly Payment', formatCurrency(results.basePayment)],
            ['Total Interest Paid', formatCurrency(results.totalInterestExtra)],
            ['Total Loan Cost', formatCurrency(results.totalCostExtra)],
            ['Time Saved', `${results.monthsSaved} months`],
            ['Interest Saved', formatCurrency(results.interestSaved)],
            ['Payoff Length', `${results.schedule.length} months`],
          ],
        },
      ],
      table: {
        heading: 'Monthly Amortization Schedule',
        head: ['Month', 'Payment', 'Principal', 'Interest', 'Balance'],
        body: results.schedule.map(row => [
          row.month,
          formatCurrency(row.payment),
          formatCurrency(row.principal),
          formatCurrency(row.interest),
          formatCurrency(row.balance),
        ]),
      },
    })
    toast.success('PDF downloaded')
  }

  return (
    <>
      <Seo
        title="Mint Loan & Amortization Calculator - Extra Payment Payoff"
        description="Free loan amortization calculator with extra principal payments. See your monthly payment, total interest, full amortization schedule, and interest saved."
        path="/calculators/mint-loan"
        schema={[
          softwareApplicationSchema('Mint Loan & Amortization Calculator', 'Calculate loan payments, interest, and full amortization schedules with extra payments.', 'https://www.mintcalculator.com/calculators/mint-loan'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Mint Loan Calculator', path: '/calculators/mint-loan' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Mint Loan Calculator', path: '/calculators/mint-loan' }]} />
        <SectionHeading
          eyebrow="Loans, Credit & Debt Payoff"
          title="Mint Loan & Amortization Calculator"
          description="Model your loan payment, total interest cost, and see how extra principal payments accelerate your payoff timeline."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5 lg:col-span-2">
            <NumberInput label="Loan amount" value={loanAmount} onChange={setLoanAmount} prefix="$" step={500} />
            <Slider label="Annual interest rate (APR)" value={apr} onChange={setApr} min={0} max={25} step={0.1} formatValue={v => `${v.toFixed(1)}%`} accent="cyan" />
            <Slider label="Loan term (years)" value={termYears} onChange={setTermYears} min={1} max={30} step={1} formatValue={v => `${v} yr`} accent="teal" />
            <NumberInput label="Extra monthly principal payment" value={extraPayment} onChange={setExtraPayment} prefix="$" step={10} accent="amber" />

            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={handleCopy} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <Copy className="h-4 w-4" />
                Copy Summary
              </button>
              <button onClick={handleReset} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
              <button onClick={handleDownloadPdf} className="flex items-center gap-2 rounded-lg border border-emerald-600/60 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-400 transition-colors hover:border-emerald-500 hover:bg-emerald-500/20">
                <Download className="h-4 w-4" />
                Download PDF
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:col-span-3 lg:grid-cols-3">
            <StatCard label="Monthly Payment" value={formatCurrency(results.basePayment)} accent="emerald" />
            <StatCard label="Total Interest" value={formatCurrency(results.totalInterestExtra)} accent="amber" />
            <StatCard label="Total Loan Cost" value={formatCurrency(results.totalCostExtra)} accent="cyan" />
            <StatCard label="Time Saved" value={`${results.monthsSaved} mo`} accent="teal" sublabel="vs. no extra payment" />
            <StatCard label="Interest Saved" value={formatCurrency(results.interestSaved)} accent="emerald" sublabel="vs. no extra payment" />
            <StatCard label="Payoff Length" value={`${results.schedule.length} mo`} accent="slate" />
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
          <button onClick={() => setShowSchedule(v => !v)} className="flex w-full items-center justify-between text-left">
            <h3 className="text-sm font-semibold text-slate-200">Full Monthly Amortization Schedule</h3>
            {showSchedule ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </button>
          {showSchedule ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4">Month</th>
                    <th className="py-2 pr-4">Payment</th>
                    <th className="py-2 pr-4">Principal</th>
                    <th className="py-2 pr-4">Interest</th>
                    <th className="py-2">Balance</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-slate-300">
                  {results.schedule.slice(0, visibleRows).map(row => (
                    <tr key={row.month} className="border-b border-slate-800/50">
                      <td className="py-2 pr-4 text-slate-500">{row.month}</td>
                      <td className="py-2 pr-4">{formatCurrency(row.payment)}</td>
                      <td className="py-2 pr-4 text-emerald-400">{formatCurrency(row.principal)}</td>
                      <td className="py-2 pr-4 text-amber-400">{formatCurrency(row.interest)}</td>
                      <td className="py-2">{formatCurrency(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {visibleRows < results.schedule.length ? (
                <button onClick={() => setVisibleRows(v => v + 24)} className="mt-4 rounded-lg border border-slate-700 px-4 py-2 text-sm text-emerald-400 hover:border-emerald-500/50">
                  Show more rows
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
          <h3 className="text-sm font-semibold text-slate-200">The Amortization Formula</h3>
          <p className="mt-3 font-mono text-sm text-slate-400">M = P × [r(1+r)^n] / [(1+r)^n − 1]</p>
          <p className="mt-2 text-sm text-slate-500">Where M is the monthly payment, P is the principal loan amount, r is the monthly interest rate (APR ÷ 12), and n is the total number of monthly payments.</p>
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default MintLoan
